import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Form, FormControl, FormField, FormItem, FormLabel, FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { MultiSelect } from "@/components/ui/multi-select";
import { TagCriteriaBuilder } from "./TagCriteriaBuilder";
import { IconSelector } from "./IconSelector";
import { useToast } from "@/hooks/use-toast";
import { useQuery } from "@tanstack/react-query";
import * as tenantsApi from "@/services/tenantsApi";
import { setTenantCache } from "@/utils/customerLookup";
import { useSingleTenant } from "@/hooks/useSingleTenant";
import { User, Filter, Info } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import * as tagsApi from "@/services/tagsApi";

const tagSchema = z.object({
  name: z.string().min(1, "Name is required").max(50, "Name must be less than 50 characters"),
  description: z.string().max(200, "Description must be less than 200 characters").optional(),
  type: z.enum(["static", "dynamic"]),
  color: z.string().regex(/^#[0-9A-F]{6}$/i, "Invalid color format"),
  icon: z.string().optional(),
  customers: z.array(z.string()),
});

type TagFormData = z.infer<typeof tagSchema>;

interface CreateTagDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onTagCreated?: () => void;
}

export function CreateTagDialog({ open, onOpenChange, onTagCreated }: CreateTagDialogProps) {
  const [criteria, setCriteria] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();
  const singleTenant = useSingleTenant();

  const { data: customers = [] } = useQuery({
    queryKey: ["tenants-map-list"],
    queryFn: async () => {
      const dtos = await tenantsApi.listTenants();
      const list = (dtos || []).map((dto: any) => ({
        id: dto.tenantId || dto.tenant_id || dto.id || "",
        name: dto.tenantName || dto.tenant_name || dto.name || "",
      })).filter((c: any) => c.id);
      setTenantCache(list);
      return list;
    },
    staleTime: 5 * 60 * 1000,
  });

  const form = useForm<TagFormData>({
    resolver: zodResolver(tagSchema),
    defaultValues: { name: "", description: "", type: "static", color: "#3B82F6", icon: "Tag", customers: [] },
  });

  const watchedType = form.watch("type");

  const handleClose = () => {
    form.reset({ name: "", description: "", type: "static", color: "#3B82F6", icon: "Tag", customers: [] });
    setCriteria(null);
    onOpenChange(false);
  };

  const customerOptions = customers.map((c) => ({ value: c.id, label: c.name }));

  const onSubmit = async (data: TagFormData) => {
    if (!singleTenant && data.customers.length === 0) {
      form.setError("customers", { message: "At least one customer must be selected" });
      return;
    }
    setIsLoading(true);
    try {
      const targetTenants = singleTenant ? [] : data.customers;
      const results = await tagsApi.createTagForTenants(targetTenants, {
        name: data.name,
        description: data.description,
        type: data.type,
        color: data.color,
        icon: data.icon ? data.icon : null,
        criteria: data.type === "dynamic" ? criteria : null,
      });

      const failures = results.filter((r) => !r.ok);
      const successes = results.length - failures.length;

      if (failures.length === 0) {
        toast({
          title: "Tag created",
          description:
            results.length > 1
              ? `${data.name} was created for ${results.length} customers.`
              : `${data.name} has been created successfully.`,
        });
        onTagCreated?.();
        handleClose();
      } else if (successes > 0) {
        toast({
          title: "Created with errors",
          description: `Created for ${successes} of ${results.length} customers. Failed: ${failures
            .map((f) => f.error)
            .join("; ")}`,
          variant: "destructive",
        });
        onTagCreated?.();
        handleClose();
      } else {
        const firstError = failures[0]?.error || "Failed to create tag.";
        throw new Error(firstError);
      }
    } catch (error: any) {
      console.error("Error creating tag:", error);
      toast({
        title: "Error",
        description: error?.message || "Failed to create tag. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = () => {
    if (watchedType === "dynamic" && !criteria) {
      toast({ title: "Rules required", description: "Please define rules for automatic tagging.", variant: "destructive" });
      return;
    }
    form.handleSubmit(onSubmit)();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: form.watch("color") }} />
            Create Tag
          </DialogTitle>
          <DialogDescription>Create a tag to organize and categorize your devices</DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form className="space-y-6">
            {!singleTenant && (
              <FormField control={form.control} name="customers" render={({ field }) => (
                <FormItem>
                  <FormLabel>Customers</FormLabel>
                  <FormControl>
                    <MultiSelect
                      options={customerOptions}
                      value={field.value}
                      onChange={(next) => form.setValue("customers", next, { shouldValidate: true })}
                      placeholder="Select customers"
                      emptyMessage="No customers available"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )} />
            )}
            <div className="grid grid-cols-[1fr,60px,60px] gap-3">
              <FormField control={form.control} name="name" render={({ field }) => (
                <FormItem><FormLabel>Tag Name</FormLabel><FormControl><Input placeholder="e.g., Marketing Laptops" {...field} /></FormControl><FormMessage /></FormItem>
              )} />
              <FormField control={form.control} name="icon" render={({ field }) => (
                <FormItem><FormLabel>Icon</FormLabel><FormControl><IconSelector value={field.value} onChange={field.onChange} placeholder="Select icon" /></FormControl><FormMessage /></FormItem>
              )} />
              <FormField control={form.control} name="color" render={({ field }) => (
                <FormItem><FormLabel>Color</FormLabel><FormControl><Input type="color" {...field} className="w-full h-10 border rounded cursor-pointer p-0" /></FormControl><FormMessage /></FormItem>
              )} />
            </div>

            <FormField control={form.control} name="description" render={({ field }) => (
              <FormItem><FormLabel>Description</FormLabel><FormControl><Textarea placeholder="What is this tag for?" {...field} rows={2} /></FormControl><FormMessage /></FormItem>
            )} />

            <FormField control={form.control} name="type" render={({ field }) => (
              <FormItem>
                <FormLabel>Tag Type</FormLabel>
                <FormControl>
                  <div className="grid grid-cols-2 gap-3">
                    <div className={`relative p-4 border-2 rounded-lg cursor-pointer transition-all hover:bg-accent/50 ${field.value === "static" ? "border-primary bg-primary/5" : "border-border hover:border-primary/50"}`} onClick={() => field.onChange("static")}>
                      <div className="flex items-center gap-3"><User className="h-5 w-5 text-primary" /><div><div className="font-medium text-sm">Static Tag</div><div className="text-xs text-muted-foreground">Manual assignment</div></div></div>
                    </div>
                    <div className={`relative p-4 border-2 rounded-lg cursor-pointer transition-all hover:bg-accent/50 ${field.value === "dynamic" ? "border-primary bg-primary/5" : "border-border hover:border-primary/50"}`} onClick={() => field.onChange("dynamic")}>
                      <div className="flex items-center gap-3">
                        <Filter className="h-5 w-5 text-primary" />
                        <div className="flex-1">
                          <div className="font-medium text-sm flex items-center gap-1">
                            Dynamic Tag
                            <TooltipProvider><Tooltip><TooltipTrigger asChild><Info className="h-3 w-3 text-muted-foreground" /></TooltipTrigger><TooltipContent side="top" className="max-w-xs"><p className="text-sm">Dynamic tags automatically assign themselves to devices that match your defined criteria.</p></TooltipContent></Tooltip></TooltipProvider>
                          </div>
                          <div className="text-xs text-muted-foreground">Rule-based</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )} />

            {watchedType === "dynamic" && (
              <div className="space-y-4 p-4 border rounded-lg bg-accent/30">
                <div className="flex items-center gap-2"><Filter className="h-4 w-4 text-primary" /><h4 className="font-medium">Tagging Rules</h4></div>
                <p className="text-sm text-muted-foreground">Define criteria to automatically assign this tag to devices that match your conditions.</p>
                <TagCriteriaBuilder value={criteria} onChange={setCriteria} />
              </div>
            )}

            <DialogFooter className="flex gap-2">
              <Button type="button" variant="outline" onClick={handleClose} disabled={isLoading}>Cancel</Button>
              <Button type="button" onClick={handleSubmit} disabled={isLoading}>{isLoading ? "Creating..." : "Create Tag"}</Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
